import dynamic from 'next/dynamic';
import type React from 'react';
import type { FunctionComponent } from 'react';

import { Layout } from '@/core/tamagoshiTailwind/components/Layout/Layout';
import { useUserTiming } from '@/domains/core/observability/utils/useUserTiming';
import { getPlatform, isB2B } from '@/domains/core/settings/utils';
import { About } from '@/domains/retention/Footer/modules/About/About';
import { International } from '@/domains/retention/Footer/modules/International/International';
import { LegalData } from '@/domains/retention/Footer/modules/LegalData/LegalData.default';
import { Navigation } from '@/domains/retention/Footer/modules/Navigation/Navigation.default';
import type { NewsletterProps } from '@/domains/retention/Footer/modules/Newsletter/Newsletter';
import { AwardsBanner } from '@/domains/retention/Footer/modules/Quality/components/AwardsBanner/AwardsBanner.default';
import { RightColumn } from '@/domains/retention/Footer/modules/RightColumn/RightColumn.default';
import type { SupportProps } from '@/domains/retention/Footer/modules/Support/Support';
import { ToTopButton } from '@/domains/retention/Footer/modules/ToTopButton/ToTopButton';

import { HOMEPAGE_PAGE_TYPE } from '../Homepage/constants';

export interface FooterProps {
  pageType?: string;
  forceB2b?: boolean;
}

const customerAreaPageType = 'customer_area';
const myOrdersPageType = 'my_orders';
const shipmentDetailsPageType = 'shipment_details';
const onlineSupportPageType = 'online_support';
const orderDetailsPageType = 'order_details';
const contactFormPageType = 'contact_form';
const helpCenterPageType = 'help_center';
const careSurveyPageType = 'survey';
const orderSelectionPageType = 'order_selection';
const contactPageType = 'contact';
const websiteUsePageType = 'website_use';
const followDeliveryType = 'follow_delivery';
const selfCareInvoiceType = 'self_care_invoice';
const refundForm = 'refund_form';

const B2CReassurance = dynamic(
  () =>
    import(
      '@/domains/retention/Homepage/b2c/modules/Reassurance/Reassurance'
    ).then((module) => module.Reassurance),
  {
    ssr: true,
    loading: () => (
      <div
        style={{ display: 'contents' }}
        suppressHydrationWarning
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html:
            document.getElementById('reassuranceContainer')?.outerHTML || '',
        }}
      />
    ),
  },
);

const B2BReassurance = dynamic(
  () =>
    import(
      '@/domains/retention/Footer/b2b/modules/Reassurance/Reassurance'
    ).then((module) => module.default),
  {
    ssr: true,
    loading: () => (
      <div
        style={{ display: 'contents' }}
        suppressHydrationWarning
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html:
            document.getElementById('reassuranceContainer')?.outerHTML || '',
        }}
      />
    ),
  },
);

const B2BConnect = dynamic<React.PropsWithChildren<unknown>>(
  () =>
    import('@/domains/retention/Footer/modules/B2BConnect/B2BConnect').then(
      (module) => module.B2BConnect,
    ),
  {
    ssr: true,
    loading: () => (
      <div
        style={{ display: 'contents' }}
        suppressHydrationWarning
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html:
            document.getElementById('b2bConnectContainer')?.outerHTML || '',
        }}
      />
    ),
  },
);

const Newsletter = dynamic<NewsletterProps>(
  () =>
    import('@/domains/retention/Footer/modules/Newsletter/Newsletter').then(
      (module) => module.Newsletter,
    ),
  {
    ssr: true,
    loading: () => (
      <div
        style={{ display: 'contents' }}
        suppressHydrationWarning
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html:
            document.getElementById('newsletterContainer')?.outerHTML || '',
        }}
      />
    ),
  },
);

const Support = dynamic<SupportProps>(
  () =>
    import('@/domains/retention/Footer/modules/Support/Support').then(
      (module) => module.Support,
    ),
  { ssr: true },
);

const B2BGermanLegal = dynamic<React.PropsWithChildren<unknown>>(
  () =>
    import(
      '@/domains/retention/Footer/modules/B2BGermanLegal/B2BGermanLegal'
    ).then((module) => module.B2BGermanLegal),
  { ssr: true },
);

export const Footer: FunctionComponent<FooterProps> = ({
  pageType,
  forceB2b = false,
}) => {
  const IS_B2B = isB2B() || forceB2b;
  const platform = getPlatform();

  const reassuranceComponent =
    isB2B() && pageType !== HOMEPAGE_PAGE_TYPE && pageType !== refundForm ? (
      <B2BReassurance />
    ) : pageType !== customerAreaPageType &&
      pageType !== myOrdersPageType &&
      pageType !== shipmentDetailsPageType &&
      pageType !== onlineSupportPageType &&
      pageType !== orderDetailsPageType &&
      pageType !== contactFormPageType &&
      pageType !== HOMEPAGE_PAGE_TYPE &&
      pageType !== helpCenterPageType &&
      pageType !== careSurveyPageType &&
      pageType !== orderSelectionPageType &&
      pageType !== followDeliveryType &&
      pageType !== selfCareInvoiceType &&
      pageType !== refundForm &&
      !IS_B2B ? (
      <B2CReassurance />
    ) : null;

  useUserTiming('footer');
  const showB2BConnect =
    pageType !== customerAreaPageType &&
    pageType !== myOrdersPageType &&
    pageType !== shipmentDetailsPageType &&
    pageType !== onlineSupportPageType &&
    pageType !== orderDetailsPageType &&
    pageType !== contactFormPageType &&
    pageType !== helpCenterPageType &&
    pageType !== careSurveyPageType &&
    pageType !== orderSelectionPageType &&
    pageType !== contactPageType &&
    pageType !== websiteUsePageType &&
    pageType !== followDeliveryType &&
    pageType !== selfCareInvoiceType &&
    pageType !== refundForm &&
    !IS_B2B;
  const showNewsletter =
    pageType !== myOrdersPageType &&
    pageType !== shipmentDetailsPageType &&
    pageType !== onlineSupportPageType &&
    pageType !== orderDetailsPageType &&
    pageType !== contactFormPageType &&
    pageType !== helpCenterPageType &&
    pageType !== careSurveyPageType &&
    pageType !== orderSelectionPageType &&
    pageType !== contactPageType &&
    pageType !== websiteUsePageType &&
    pageType !== followDeliveryType &&
    pageType !== selfCareInvoiceType &&
    pageType !== refundForm;

  return (
    <>
      {reassuranceComponent}
      <footer className="bg-foundation-quinary text-foundation-inverted">
        {showB2BConnect && <B2BConnect />}
        {showNewsletter && <Newsletter theme={IS_B2B ? 'pro' : 'default'} />}
        <Layout>
          <div
            className="relative inline-block w-full py-xl
              [grid-template-areas:'contact_about_other''awardsBanner_awardsBanner_awardsBanner']
              desktop:grid desktop:grid-cols-[1fr_1fr_1fr] wide:gap-l"
          >
            <div className="w-full [grid-area:other] desktop:max-w-full">
              <RightColumn />
            </div>
            <div className="w-full [grid-area:contact] desktop:max-w-full">
              <Navigation />
              {IS_B2B && <Support className="hidden desktop:inline-block" />}
            </div>
            <div
              className={`mt-[12px] w-full [grid-area:about] desktop:mt-0
                desktop:max-w-full`}
            >
              <About />
              <International />
            </div>
            <ToTopButton />
          </div>
        </Layout>
        {IS_B2B && platform === 'de' && <B2BGermanLegal />}
        <div className="w-full [grid-area:awardsBanner]">
          <AwardsBanner />
        </div>
        <div className="bg-foundation-quinary p-m tablet:text-center">
          <LegalData />
        </div>
      </footer>
    </>
  );
};

Footer.displayName = 'Footer';
